import { useState } from 'react';
import { Card } from 'src/components/UI/Card';
import { IconButton } from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import TitleTooltip from 'src/components/UI/TitleTooltip';
import { KeywordAssistPageDetail } from '../../types/keyword-assist.type';

interface Props {
  meta: KeywordAssistPageDetail;
}

const CopyIcon = () => (
  <img
    src="/static/images/icons/copyIcon.svg"
    className="w-4 h-4 shrink-0 cursor-pointer select-none"
    alt="Copy"
  />
);

const CheckIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth="2.5"
    stroke="#12B76A"
    className="w-4 h-4 shrink-0"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M4.5 12.75l6 6 9-13.5"
    />
  </svg>
);

// Generates paginated suggestions based on selected item variations
const getSuggestionsForPage = (
  meta: KeywordAssistPageDetail,
  page: number
): { suggestions: any[]; totalPages: number } => {
  const suggestionsList = meta?.keyword_suggestions || [];
  const pageLength = 4;
  const totalPages = Math.ceil(suggestionsList.length / pageLength);

  return {
    suggestions: suggestionsList
      .slice((page - 1) * pageLength, page * pageLength)
      .map((v) => ({ text: v })),
    totalPages: totalPages
  };
};

export default function KAKeywordSuggestionsCard({ meta }: Props) {
  const [currentPage, setCurrentPage] = useState(1);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const { suggestions, totalPages } = getSuggestionsForPage(meta, currentPage);
  const displayPage = totalPages === 0 ? 0 : currentPage;

  const handlePrevPage = () => {
    if (totalPages <= 1) return;
    setCurrentPage((prev) => (prev > 1 ? prev - 1 : totalPages));
  };

  const handleNextPage = () => {
    if (totalPages <= 1) return;
    setCurrentPage((prev) => (prev < totalPages ? prev + 1 : 1));
  };

  const handleCopy = (text: string, index: number) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => {
      setCopiedIndex(null);
    }, 1500);
  };

  return (
    <Card className="flex flex-col justify-between h-full">
      <div className="px-4 sm:px-6">
        <div className="flex justify-between items-start gap-2 mb-2">
          <div className="min-w-0 flex-1">
            <TitleTooltip>
              <TitleTooltip.Text
                title="Keyword Suggestions"
                className="!text-[#475467] !font-medium !text-base leading-snug"
              />
            </TitleTooltip>
          </div>
          <div className="flex items-center gap-2 shrink-0 pt-0.5">
            <span className="text-sm font-medium text-[#475467] whitespace-nowrap">
              {displayPage} / {totalPages}
            </span>
            <div className="flex items-center gap-0.5">
              <IconButton
                onClick={handlePrevPage}
                disabled={totalPages <= 1}
                size="small"
                sx={{ p: 0.5 }}
              >
                <ArrowBackIosNewIcon
                  sx={{
                    fontSize: 14,
                    color: totalPages <= 1 ? '#D0D5DD' : 'var(--color-primary)'
                  }}
                />
              </IconButton>
              <IconButton
                onClick={handleNextPage}
                disabled={totalPages <= 1}
                size="small"
                sx={{ p: 0.5 }}
              >
                <ArrowForwardIosIcon
                  sx={{
                    fontSize: 14,
                    color: totalPages <= 1 ? '#D0D5DD' : 'var(--color-primary)'
                  }}
                />
              </IconButton>
            </div>
          </div>
        </div>

        <p className="text-sm text-[#475467] mb-2">Try these suggestions:</p>

        {/* Suggestion list */}
        <div className="space-y-2">
          {suggestions && suggestions.length > 0 ? (
            suggestions.map((s, idx) => {
              const itemIndex = (currentPage - 1) * 4 + idx;
              return (
                <div key={idx} className="flex flex-col">
                  <div className="bg-[#F8F9FC] border border-[#EAECF0] rounded-xl px-3.5 py-2.5 flex justify-between items-center min-w-0 gap-2">
                    <span className="text-sm font-medium text-[#1D2939] truncate min-w-0">
                      "{s.text}"
                    </span>
                    <button
                      onClick={() => handleCopy(s.text, itemIndex)}
                      className="flex items-center justify-center shrink-0 w-6 h-6 rounded-md hover:bg-black/5 transition-colors focus:outline-none"
                      title="Copy keyword"
                    >
                      {copiedIndex === itemIndex ? <CheckIcon /> : <CopyIcon />}
                    </button>
                  </div>
                  {/* <div className="flex items-center gap-1 mt-1.5 text-xs font-semibold text-[#12B76A]">
                    <TrendUpIcon />
                    <span>{s.change} Est. Change</span>
                  </div> */}
                </div>
              );
            })
          ) : (
            <div className="bg-[#F8F9FC] border border-[#EAECF0] rounded-xl px-4 py-6 text-center text-sm font-medium text-[#667085]">
              No data available
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
